import { v7 as uuidV7 } from 'uuid';

/**
 * The anonymous per-browser identifier a referral claim is written against.
 *
 * Shape (GOTU-7240): `ref_` + a UUIDv7, e.g.
 * `ref_0192f3c1-8a4e-7c2b-9d31-6f0a1b2c3d4e` — 40 characters, inside the
 * column's `varchar(64)`. Mirrors mobile-app `referralIdentifier.ts`, with
 * `localStorage` standing in for MMKV.
 *
 * - **v7, not v4.** v7 is time-ordered, so claim rows cluster by mint time on
 *   `referral_claim_identifier_idx` instead of scattering across it.
 * - **The `ref_` prefix is load-bearing.** It is what discriminates a v2
 *   identifier from a legacy Mongo id during the cutover straddle.
 * - **Minted once and persisted.** A reload or a later visit reuses it, which
 *   is what lets the server's same-day `(identifier, code)` window dedupe.
 * - **Never logged and never used as an analytics dimension.** It is a
 *   per-browser value deliberately not allowed to double as a fingerprint.
 */
const REFERRAL_IDENTIFIER_KEY = 'referralIdentifier';

const REFERRAL_IDENTIFIER_PREFIX = 'ref_';

export const getOrCreateReferralIdentifier = (): string => {
  try {
    const stored = localStorage.getItem(REFERRAL_IDENTIFIER_KEY);
    if (stored) return stored;
  } catch {
    // Fall through and mint. A read fault (private mode, blocked storage)
    // costs a duplicate identifier at worst; the claim table has no
    // uniqueness by design.
  }

  const identifier = `${REFERRAL_IDENTIFIER_PREFIX}${uuidV7()}`;

  try {
    localStorage.setItem(REFERRAL_IDENTIFIER_KEY, identifier);
  } catch {
    // Best effort: an unpersisted identifier still lets this claim be written.
  }

  return identifier;
};
